import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useState, useCallback, type ReactNode } from 'react'
import { Card } from '../components/Card'
import { InfoIcon, LearnTip } from '../lib/learn-mode'
import { fmtMoney, FMT_INT } from '../lib/money'
import { useCurrency } from '../lib/currency-context'
import { STANDARD_PERIODS, type PeriodKey } from '../lib/period-context'
import { BarChart } from '../components/charts/BarChart'

interface CashKPIs {
  operating_cents: number
  deposit_churn_cents: number
  carrier_cents: number
  carrier_split: { usps_pct: number; dhl_pct: number } | null
  latest_balance_cents: number | null
  latest_balance_date: string | null
  period: { from: string; to: string }
}

interface FlowRow { day: string; kind: string; amount: number }
interface FlowResponse {
  period: { from: string; to: string; label: string }
  bucket: string
  operating_only: boolean
  rows: FlowRow[]
}

interface ReconHealth {
  chase: { rows: number; classified: number; last_upload: string | null }
  stripe: { rows: number; last_pull: string | null } | null
  payouts_matched: { matched: number; total: number } | null
}

interface CatRow {
  id: number; posting_date: string; type: string; amount_cents: number;
  counterparty_kind: string | null; counterparty_canonical: string | null;
  business_purpose: string | null; description_preview: string;
}
interface CategorizationResponse {
  rows: CatRow[]
  counts: { unknown: number; unparsed: number; total: number }
}

interface TxRow {
  id: number; posting_date: string; type: string; amount_cents: number;
  counterparty_kind: string | null; counterparty_canonical: string | null;
  business_purpose: string | null; description_preview: string;
  balance_cents: number | null;
}
interface TxResponse {
  rows: TxRow[]; total_rows: number;
  sum_in_cents: number; sum_out_cents: number;
  limit: number; offset: number;
}
interface FilterOption { kind?: string; purpose?: string; count: number }
interface TxFilters { kinds: FilterOption[]; purposes: FilterOption[] }

// ---- Stripe types ----
interface StripeTxRow {
  id: string; created: string; type: string; reporting_category: string | null;
  amount_cents: number; fee_cents: number; net_cents: number;
  currency: string; source_id: string | null; description: string | null;
  status: string | null;
}
interface StripeTxResponse {
  rows: StripeTxRow[]; total_rows: number;
  sum_in_cents: number; sum_out_cents: number; sum_fees_cents: number;
  limit: number; offset: number;
}
interface StripeFilterOption { type?: string; category?: string; count: number }
interface StripeFilters { types: StripeFilterOption[]; categories: StripeFilterOption[] }

// Auto-pick bucket granularity based on period length
function autoBucket(period: PeriodKey): 'daily' | 'weekly' | 'monthly' {
  if (['today', 'this_week', 'last_2_weeks', 'this_month'].includes(period)) return 'daily'
  if (period === 'this_quarter') return 'weekly'
  return 'monthly'
}

function fmtLabel(day: string, bucket: 'daily' | 'weekly' | 'monthly'): string {
  const d = new Date(day + 'T00:00:00Z')
  if (bucket === 'monthly') return d.toLocaleString('en', { month: 'short', timeZone: 'UTC' })
  const mo = d.toLocaleString('en', { month: 'short', timeZone: 'UTC' })
  return `${mo} ${d.getUTCDate()}`
}

export function CashView() {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  // Single period controls both KPI tiles and the chart
  const [period, setPeriod] = useState<PeriodKey>('this_month')
  const periodLabel = STANDARD_PERIODS.find(p => p.key === period)?.label ?? period

  const kpis = useQuery<CashKPIs>({
    queryKey: ['cash', 'kpis', period],
    queryFn: () => fetch(`/api/cash/kpis?period=${period}`).then(r => r.json()),
  })
  const flow = useQuery<FlowResponse>({
    queryKey: ['cash', 'flow', period],
    queryFn: () => {
      const bucket = autoBucket(period)
      return fetch(`/api/cash/flow?period=${period}&bucket=${bucket}&operating_only=false`).then(r => r.json())
    },
  })
  const health = useQuery<ReconHealth>({
    queryKey: ['cash', 'recon'],
    queryFn: () => fetch('/api/cash/recon-health').then(r => r.json()),
  })
  const cats = useQuery<CategorizationResponse>({
    queryKey: ['cash', 'categorization'],
    queryFn: () => fetch('/api/cash/categorization?limit=20').then(r => r.json()),
  })

  const k = kpis.data
  const h = health.data

  // Aggregate flow rows → {in, out} per bucket
  const chartData = (() => {
    if (!flow.data?.rows.length) return null
    const bucket = autoBucket(period)
    const days = Array.from(new Set(flow.data.rows.map(r => r.day))).sort()
    const byDay = new Map<string, { in: number; out: number }>()
    days.forEach(d => byDay.set(d, { in: 0, out: 0 }))
    flow.data.rows.forEach(r => {
      const cents = Number(r.amount)
      const entry = byDay.get(r.day)!
      if (cents > 0) entry.in  += cents / 100
      else           entry.out += Math.abs(cents) / 100
    })
    const inData  = days.map(d => byDay.get(d)!.in)
    const outData = days.map(d => byDay.get(d)!.out)
    const totalIn  = inData.reduce((a, b) => a + b, 0)
    const totalOut = outData.reduce((a, b) => a + b, 0)
    return {
      labels: days.map(d => fmtLabel(d, bucket)),
      inData, outData,
      totalIn, totalOut,
      net: totalIn - totalOut,
    }
  })()

  return (
    <div className="space-y-5">
      {/* Hero */}
      <Card className="p-7">
        <div className="flex items-center justify-between gap-6">
          <div>
            <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">
              {t('cash.latest_chase_balance')}
              <InfoIcon tip="Most recent posted balance in your Chase account. Doesn't include Stripe pending payouts." />
            </div>
            <div className="mt-2 text-[44px] font-bold text-ink-900 leading-none">
              {kpis.isLoading ? <span className="text-ink-300">$—,———</span>
                : fmtMoney(k?.latest_balance_cents ?? 0, currency, { decimals: 0 })}
            </div>
            <div className="mt-2 text-[13px] text-ink-500">{t('cash.as_of')} {k?.latest_balance_date ?? '—'}</div>
            <LearnTip>{t('cash.latest_chase_learn')}</LearnTip>
          </div>
        </div>
      </Card>

      {/* KPI header with shared period selector */}
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">
          {t('cash.key_metrics')}
        </div>
        <select
          value={period}
          onChange={e => setPeriod(e.target.value as PeriodKey)}
          className="px-2.5 py-1 border border-ink-200 rounded-md text-[12px] bg-white font-medium"
        >
          {STANDARD_PERIODS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('cash.operating_cash')}
            <InfoIcon tip={t('cash.operating_cash_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">{k ? fmtMoney(k.operating_cents, currency, { compact: true }) : '—'}</div>
          <div className="text-[11px] text-ink-500 mt-1">{periodLabel} · {t('cash.excl_deposit_churn')}</div>
          <LearnTip>{t('cash.operating_cash_learn')}</LearnTip>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('cash.deposit_churn')}
            <InfoIcon tip={t('cash.deposit_churn_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">{k ? fmtMoney(k.deposit_churn_cents, currency, { compact: true }) : '—'}</div>
          <div className="text-[11px] text-ink-500 mt-1">{periodLabel} · {t('cash.in_out_via_deposits')}</div>
          <LearnTip>{t('cash.deposit_churn_learn')}</LearnTip>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('cash.carrier_spend')}
            <InfoIcon tip={t('cash.carrier_spend_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">{k ? fmtMoney(k.carrier_cents, currency, { compact: true }) : '—'}</div>
          <div className="text-[11px] text-ink-500 mt-1">
            {k?.carrier_split ? `USPS ${k.carrier_split.usps_pct}% · DHL ${k.carrier_split.dhl_pct}%` : '—'}
          </div>
          <LearnTip>{t('cash.carrier_spend_learn')}</LearnTip>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('cash.categorization_health')}
            <InfoIcon tip={t('cash.categorization_health_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">
            {h ? `${Math.round((h.chase.classified / Math.max(h.chase.rows, 1)) * 100)}%` : '—'}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">
            {h ? `${FMT_INT.format(h.chase.classified)} of ${FMT_INT.format(h.chase.rows)} rows` : '—'}
          </div>
          <LearnTip>{t('cash.categorization_health_learn')}</LearnTip>
        </Card>
      </div>

      {/* Cash flow chart */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[14px] font-semibold text-ink-800">
              {t('cash.money_in_vs_out')}
              <InfoIcon tip={t('cash.money_in_vs_out_tip')} />
            </div>
            {chartData && (
              <div className="flex gap-5 mt-1.5">
                <span className="text-[12px]">
                  <span className="text-ink-500">{t('cash.in')} </span>
                  <span className="font-semibold text-mint-600">{fmtMoney(chartData.totalIn * 100, currency, { compact: true })}</span>
                </span>
                <span className="text-[12px]">
                  <span className="text-ink-500">{t('cash.out')} </span>
                  <span className="font-semibold text-rose-600">{fmtMoney(chartData.totalOut * 100, currency, { compact: true })}</span>
                </span>
                <span className="text-[12px]">
                  <span className="text-ink-500">{t('cash.net')} </span>
                  <span className={`font-semibold ${chartData.net >= 0 ? 'text-mint-600' : 'text-rose-600'}`}>
                    {chartData.net < 0 ? '-' : ''}{fmtMoney(Math.abs(chartData.net) * 100, currency, { compact: true })}
                  </span>
                </span>
              </div>
            )}
          </div>
          <div className="text-[12px] text-ink-400 font-medium">{periodLabel}</div>
        </div>
        {flow.isLoading
          ? <div className="flex items-center justify-center h-[280px] text-ink-400">{t('common.loading')}</div>
          : chartData
            ? <BarChart
                labels={chartData.labels}
                series={[
                  { label: 'Money in',  data: chartData.inData,  color: '#10b981' },
                  { label: 'Money out', data: chartData.outData, color: '#f87171' },
                ]}
                height={280}
                barThickness={autoBucket(period) === 'daily' ? 14 : autoBucket(period) === 'weekly' ? 22 : 32}
                yTickFormat={v => v >= 1000 ? '$' + Math.round(v / 1000) + 'k' : '$' + Math.round(v)}
              />
            : <div className="flex items-center justify-center h-[280px] text-ink-400 text-[13px]">{t('cash.no_data_period')}</div>
        }
      </Card>

      {/* Sync health */}
      <Card className="p-5">
        <div className="text-[14px] font-semibold text-ink-800 mb-3">
          {t('cash.data_sources_sync')}
          <InfoIcon tip={t('cash.data_sources_tip')} />
        </div>
        <div className="space-y-2">
          <SourceRow label="Chase" status={h?.chase.rows ? 'green' : 'red'}
            line={h ? `${FMT_INT.format(h.chase.rows)} transactions · ${Math.round((h.chase.classified / Math.max(h.chase.rows, 1)) * 100)}% categorized · last upload ${h.chase.last_upload ?? '—'}` : 'Loading…'}
            pill={h?.chase.rows ? 'healthy' : 'missing'} />
          <SourceRow label="Stripe" status={h?.stripe?.rows ? 'green' : 'amber'}
            line={h?.stripe ? `${FMT_INT.format(h.stripe.rows)} balance transactions · last pull ${h.stripe.last_pull ?? '—'}` : 'Not ingested yet · run python scripts/import_stripe.py --all'}
            pill={h?.stripe?.rows ? 'live' : 'pending'} />
          <SourceRow
            label="Stripe payouts → Chase"
            status={h?.payouts_matched
              ? (h.payouts_matched.matched === h.payouts_matched.total ? 'green' : 'amber')
              : 'red'}
            line={h?.payouts_matched
              ? `${h.payouts_matched.matched} of ${h.payouts_matched.total} Stripe payouts linked to their Chase deposit · ${h.payouts_matched.total - h.payouts_matched.matched} pending link`
              : 'Matching not yet run'}
            pill={h?.payouts_matched
              ? (h.payouts_matched.matched === h.payouts_matched.total ? 'all matched' : `${h.payouts_matched.total - h.payouts_matched.matched} to link`)
              : 'pending'} />
        </div>
      </Card>

      {/* Chase + Stripe transactions browser */}
      <TransactionsBrowser />

      {/* Categorization review */}
      <details className="rounded-lg border border-ink-200 bg-white">
        <summary className="px-5 py-3.5 cursor-pointer font-semibold text-ink-800 list-none flex items-center justify-between hover:bg-ink-50">
          <span>
            {t('cash.categorization_review')}
            <span className="ml-3 text-[11px] font-normal text-ink-500">
              {cats.data && `${cats.data.counts.unknown} unknown · ${cats.data.counts.unparsed} unparsed · ${FMT_INT.format(cats.data.counts.total)} total rows`}
            </span>
          </span>
          <span className="text-[10px] text-ink-400">{t('cash.click_to_expand')}</span>
        </summary>
        <div className="px-5 pb-5">
          {cats.data && (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                  <th className="text-left py-2 pr-2">{t('cash.date_col')}</th>
                  <th className="text-left py-2 pr-2">{t('cash.type_col')}</th>
                  <th className="text-right py-2 pr-2">{t('cash.amount_col')}</th>
                  <th className="text-left py-2 pr-2">{t('cash.kind_col')}</th>
                  <th className="text-left py-2 pr-2">{t('cash.canonical_col')}</th>
                  <th className="text-left py-2 pr-2">{t('cash.purpose_col')}</th>
                </tr>
              </thead>
              <tbody>
                {cats.data.rows.map(r => (
                  <tr key={r.id} className="border-b border-ink-100 hover:bg-ink-50">
                    <td className="py-2 pr-2">{r.posting_date}</td>
                    <td className="py-2 pr-2 text-ink-500">{r.type}</td>
                    <td className={`py-2 pr-2 text-right num font-medium ${r.amount_cents < 0 ? 'text-flame-600' : 'text-mint-600'}`}>
                      {fmtMoney(r.amount_cents, currency)}
                    </td>
                    <td className="py-2 pr-2"><span className={`pill ${pillForKind(r.counterparty_kind)}`}>{r.counterparty_kind ?? '—'}</span></td>
                    <td className="py-2 pr-2 text-[12px]">{r.counterparty_canonical ?? '—'}</td>
                    <td className="py-2 pr-2 text-[11px] text-ink-500">{r.business_purpose ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </details>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tabbed browser — Chase and Stripe side by side
// ---------------------------------------------------------------------------
function TransactionsBrowser() {
  const [tab, setTab] = useState<'chase' | 'stripe'>('chase')
  return (
    <div>
      <div className="flex gap-0.5 mb-[-1px] relative z-10 px-1">
        <TabButton active={tab === 'chase'}  onClick={() => setTab('chase')}>Chase</TabButton>
        <TabButton active={tab === 'stripe'} onClick={() => setTab('stripe')}>Stripe</TabButton>
      </div>
      {tab === 'chase'  && <ChaseTransactions />}
      {tab === 'stripe' && <StripeTransactions />}
    </div>
  )
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-[13px] font-semibold rounded-t-lg border border-b-0 transition-colors
        ${active
          ? 'bg-white border-ink-200 text-ink-900'
          : 'bg-ink-50 border-transparent text-ink-500 hover:text-ink-700 hover:bg-ink-100'}`}
    >
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Stripe transactions browser
// ---------------------------------------------------------------------------
function StripeTransactions() {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const PAGE = 100

  const today       = new Date().toISOString().slice(0, 10)
  const firstOfYear = `${new Date().getFullYear()}-01-01`

  const [from, setFrom]         = useState(firstOfYear)
  const [to, setTo]             = useState(today)
  const [type, setType]         = useState('all')
  const [category, setCategory] = useState('all')
  const [direction, setDir]     = useState('all')
  const [search, setSearch]     = useState('')
  const [offset, setOffset]     = useState(0)

  const resetOffset = useCallback(() => setOffset(0), [])

  const buildUrl = (off = offset) => {
    const p = new URLSearchParams({ from, to, limit: String(PAGE), offset: String(off) })
    if (type !== 'all')      p.set('type', type)
    if (category !== 'all')  p.set('reporting_category', category)
    if (direction !== 'all') p.set('direction', direction)
    if (search.trim())       p.set('search', search.trim())
    return `/api/cash/stripe-transactions?${p}`
  }

  const qKey = ['stripe', 'txns', from, to, type, category, direction, search, offset]

  const txns = useQuery<StripeTxResponse>({
    queryKey: qKey,
    queryFn: () => fetch(buildUrl()).then(r => r.json()),
  })
  const filters = useQuery<StripeFilters>({
    queryKey: ['stripe', 'tx-filters'],
    queryFn: () => fetch('/api/cash/stripe-transaction-filters').then(r => r.json()),
    staleTime: 5 * 60_000,
  })

  const d = txns.data
  const totalPages  = d ? Math.ceil(d.total_rows / PAGE) : 0
  const currentPage = Math.floor(offset / PAGE) + 1

  return (
    <div className="rounded-b-lg rounded-tr-lg border border-ink-200 bg-white p-5">
      <div className="text-[14px] font-semibold text-ink-800 mb-4">
        {t('cash.stripe_balance_transactions')}
        <span className="ml-2 text-[11px] font-normal text-ink-400">stripe_balance_transactions ledger</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <input type="date" value={from} onChange={e => { setFrom(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white" />
        <span className="self-center text-ink-400 text-[12px]">{t('cash.to')}</span>
        <input type="date" value={to} onChange={e => { setTo(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white" />

        <select value={type} onChange={e => { setType(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white font-medium">
          <option value="all">{t('cash.all_types')}</option>
          {filters.data?.types.map(t => (
            <option key={t.type} value={t.type!}>{t.type} ({FMT_INT.format(t.count)})</option>
          ))}
        </select>

        <select value={category} onChange={e => { setCategory(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white font-medium min-w-[160px]">
          <option value="all">{t('cash.all_categories')}</option>
          {filters.data?.categories.map(c => (
            <option key={c.category} value={c.category!}>{c.category} ({FMT_INT.format(c.count)})</option>
          ))}
        </select>

        <select value={direction} onChange={e => { setDir(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white font-medium">
          <option value="all">{t('cash.in_plus_out')}</option>
          <option value="in">{t('cash.in_only')}</option>
          <option value="out">{t('cash.out_only')}</option>
        </select>

        <input type="text" placeholder={t('cash.search_stripe')} value={search}
          onChange={e => { setSearch(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white w-56" />

        <button type="button" onClick={() => { setFrom(firstOfYear); setTo(today); setType('all'); setCategory('all'); setDir('all'); setSearch(''); setOffset(0) }}
          className="h-8 px-3 border border-ink-200 text-ink-500 rounded-md text-[12px] hover:bg-ink-50 transition-colors">Clear</button>
      </div>

      {/* Summary bar */}
      {d && (
        <div className="flex gap-6 mb-4 px-3 py-2.5 bg-ink-50 rounded-lg text-[12px]">
          <span><span className="text-ink-500">Rows:</span> <strong>{FMT_INT.format(d.total_rows)}</strong></span>
          <span><span className="text-ink-500">Gross in:</span> <strong className="text-mint-600">{fmtMoney(Number(d.sum_in_cents), currency)}</strong></span>
          <span><span className="text-ink-500">Gross out:</span> <strong className="text-rose-600">{fmtMoney(Number(d.sum_out_cents), currency)}</strong></span>
          <span><span className="text-ink-500">Fees:</span> <strong className="text-amber-600">{fmtMoney(Number(d.sum_fees_cents), currency)}</strong></span>
        </div>
      )}

      {txns.isLoading
        ? <div className="flex items-center justify-center h-32 text-ink-400 text-[13px]">{t('common.loading')}</div>
        : !d?.total_rows && !txns.isLoading
          ? (
            <div className="flex items-center justify-center h-32 text-ink-400 text-[13px]">
              No Stripe data — run <code className="ml-1 px-1 py-0.5 bg-ink-100 rounded text-[11px]">python scripts/import_stripe.py --all</code>
            </div>
          )
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                    <th className="text-left py-2 pr-3">{t('cash.date_col')}</th>
                    <th className="text-left py-2 pr-3">{t('cash.type_col')}</th>
                    <th className="text-left py-2 pr-3">{t('cash.category_col')}</th>
                    <th className="text-right py-2 pr-3">{t('cash.amount_col')}</th>
                    <th className="text-right py-2 pr-3">{t('cash.fee_col')}</th>
                    <th className="text-right py-2 pr-3 font-bold">{t('cash.net_col')}</th>
                    <th className="text-left py-2 pr-3">Description</th>
                    <th className="text-left py-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {d?.rows.map(r => (
                    <tr key={r.id} className="border-b border-ink-100 hover:bg-ink-50">
                      <td className="py-1.5 pr-3 whitespace-nowrap">{r.created}</td>
                      <td className="py-1.5 pr-3">
                        <span className={`pill ${stripeTypePill(r.type)}`}>{r.type}</span>
                      </td>
                      <td className="py-1.5 pr-3 text-ink-500 text-[11px]">{r.reporting_category ?? '—'}</td>
                      <td className={`py-1.5 pr-3 text-right num font-medium whitespace-nowrap ${r.amount_cents < 0 ? 'text-rose-600' : 'text-mint-600'}`}>
                        {fmtMoney(r.amount_cents, currency)}
                      </td>
                      <td className="py-1.5 pr-3 text-right num text-amber-600 whitespace-nowrap">
                        {r.fee_cents !== 0 ? fmtMoney(-r.fee_cents, currency) : '—'}
                      </td>
                      <td className={`py-1.5 pr-3 text-right num font-semibold whitespace-nowrap ${r.net_cents < 0 ? 'text-rose-700' : 'text-ink-800'}`}>
                        {fmtMoney(r.net_cents, currency)}
                      </td>
                      <td className="py-1.5 pr-3 text-ink-400 max-w-[220px] truncate" title={r.description ?? ''}>
                        {r.description || '—'}
                      </td>
                      <td className="py-1.5 text-ink-400 text-[11px] font-mono max-w-[120px] truncate" title={r.source_id ?? ''}>
                        {r.source_id ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      }

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-ink-100">
          <button onClick={() => setOffset(Math.max(0, offset - PAGE))} disabled={offset === 0}
            className="px-3 py-1.5 border border-ink-200 rounded-md text-[12px] font-medium disabled:opacity-40 hover:bg-ink-50">
            {t('common.previous')}
          </button>
          <span className="text-[12px] text-ink-500">
            {t('cash.page_of', { current: currentPage, total: totalPages, rows: FMT_INT.format(d?.total_rows ?? 0) })}
          </span>
          <button onClick={() => setOffset(offset + PAGE)} disabled={currentPage >= totalPages}
            className="px-3 py-1.5 border border-ink-200 rounded-md text-[12px] font-medium disabled:opacity-40 hover:bg-ink-50">
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  )
}

function stripeTypePill(type: string) {
  const map: Record<string, string> = {
    charge:          'bg-mint-100 text-mint-800',
    payment:         'bg-mint-100 text-mint-800',
    refund:          'bg-rose-100 text-rose-800',
    payout:          'bg-teal-100 text-teal-800',
    adjustment:      'bg-amber-100 text-amber-800',
    dispute:         'bg-rose-100 text-rose-800',
    stripe_fee:      'bg-yellow-100 text-yellow-800',
    transfer:        'bg-slate-100 text-slate-600',
    application_fee: 'bg-purple-100 text-purple-800',
  }
  return map[type] ?? 'bg-ink-100 text-ink-500'
}

// ---------------------------------------------------------------------------
// Classification buckets
// ---------------------------------------------------------------------------
const BUCKETS: { label: string; kind: string; purpose: string }[] = [
  { label: 'Personnel — contractor salary',     kind: 'ops',         purpose: 'contractor_salary' },
  { label: 'Personnel — consulting',            kind: 'ops',         purpose: 'contractor_salary_or_consulting' },
  { label: 'Personnel — software contractor',   kind: 'ops',         purpose: 'contractor_payment_software' },
  { label: 'Infrastructure — cloud',            kind: 'ops',         purpose: 'cloud_infrastructure' },
  { label: 'Infrastructure — software sub',     kind: 'ops',         purpose: 'software_subscription' },
  { label: 'Infrastructure — domain/hosting',   kind: 'ops',         purpose: 'domain_hosting' },
  { label: 'Affiliate payout',                  kind: 'affiliate',   purpose: 'affiliate_payout' },
  { label: 'Carrier — invoice payment',         kind: 'carrier',     purpose: 'carrier_invoice_payment' },
  { label: 'Bank fee',                          kind: 'fee',         purpose: 'bank_service_charge' },
  { label: 'Owner distribution',                kind: 'owner',       purpose: 'owner_distribution' },
  { label: 'Shareholder — loan repayment',      kind: 'shareholder', purpose: 'shareholder_loan_repayment' },
  { label: 'Customer deposit',                  kind: 'customer',    purpose: 'customer_deposit' },
]

// Always show classify button so already-classified rows can be reclassified
function needsClassify(_r: TxRow) { return true }

function pillForKind(kind: string | null) {
  const map: Record<string, string> = {
    customer:    'bg-mint-100 text-mint-800',
    transfer:    'bg-teal-100 text-teal-800',
    carrier:     'bg-rose-100 text-rose-800',
    ops:         'bg-amber-100 text-amber-800',
    affiliate:   'bg-purple-100 text-purple-800',
    shareholder: 'bg-slate-100 text-slate-600',
    owner:       'bg-slate-100 text-slate-400',
    fee:         'bg-yellow-100 text-yellow-800',
    unknown:     'bg-ink-100 text-ink-500',
  }
  return map[kind ?? 'unknown'] ?? 'bg-ink-100 text-ink-500'
}

// ---------------------------------------------------------------------------
// Inline classify panel
// ---------------------------------------------------------------------------
function ClassifyPanel({ row, onDone, onCancel }: {
  row: TxRow
  onDone: (updated: Partial<TxRow>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [bucketIdx, setBucketIdx] = useState<number>(-1)
  const [canonical, setCanonical] = useState(row.counterparty_canonical ?? '')
  const [saving, setSaving]       = useState(false)
  const [err, setErr]             = useState('')

  const selectedBucket = bucketIdx >= 0 ? BUCKETS[bucketIdx] : null

  async function save() {
    if (!selectedBucket) { setErr('Pick a bucket first.'); return }
    if (!canonical.trim()) { setErr('Enter a counterparty name.'); return }
    setSaving(true); setErr('')
    try {
      const res = await fetch(`/api/cash/transactions/${row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind:      selectedBucket.kind,
          canonical: canonical.trim(),
          purpose:   selectedBucket.purpose,
        }),
      })
      if (!res.ok) throw new Error(await res.text())
      onDone({
        counterparty_kind:      selectedBucket.kind,
        counterparty_canonical: canonical.trim(),
        business_purpose:       selectedBucket.purpose,
      })
    } catch (e: unknown) {
      setErr((e as Error).message ?? 'Save failed')
      setSaving(false)
    }
  }

  return (
    <tr>
      <td colSpan={8} className="px-3 pb-3 pt-1 bg-amber-50 border-b border-amber-200">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <div className="text-[10px] font-semibold text-ink-500 uppercase mb-1">Bucket</div>
            <select value={bucketIdx} onChange={e => setBucketIdx(Number(e.target.value))}
              className="px-2.5 py-1.5 border border-ink-300 rounded-md text-[12px] bg-white min-w-[240px]">
              <option value={-1}>select bucket</option>
              {BUCKETS.map((b, i) => <option key={i} value={i}>{b.label}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-ink-500 uppercase mb-1">Counterparty name</div>
            <input value={canonical} onChange={e => setCanonical(e.target.value)}
              placeholder={t('cash.counterparty_placeholder')}
              className="px-2.5 py-1.5 border border-ink-300 rounded-md text-[12px] bg-white w-56" />
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={save} disabled={saving}
              className="px-3 py-1.5 bg-mint-500 text-white rounded-md text-[12px] font-semibold disabled:opacity-50 hover:bg-mint-600">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={onCancel}
              className="px-3 py-1.5 border border-ink-200 rounded-md text-[12px] font-medium hover:bg-ink-50">
              {t('common.cancel')}
            </button>
            {err && <span className="text-[11px] text-rose-600">{err}</span>}
          </div>
        </div>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Chase transactions browser
// ---------------------------------------------------------------------------
function ChaseTransactions() {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const qc  = useQueryClient()
  const PAGE = 100

  const today       = new Date().toISOString().slice(0, 10)
  const firstOfYear = `${new Date().getFullYear()}-01-01`

  const [from, setFrom]       = useState(firstOfYear)
  const [to, setTo]           = useState(today)
  const [kind, setKind]       = useState('all')
  const [purpose, setPurpose] = useState('all')
  const [direction, setDir]   = useState('all')
  const [search, setSearch]   = useState('')
  const [offset, setOffset]   = useState(0)
  const [classifyId, setClassifyId] = useState<number | null>(null)

  const resetOffset = useCallback(() => setOffset(0), [])

  const buildUrl = (off = offset) => {
    const p = new URLSearchParams({ from, to, limit: String(PAGE), offset: String(off) })
    if (kind !== 'all')      p.set('kind', kind)
    if (purpose !== 'all')   p.set('purpose', purpose)
    if (direction !== 'all') p.set('direction', direction)
    if (search.trim())       p.set('search', search.trim())
    return `/api/cash/transactions?${p}`
  }

  const qKey = ['cash', 'txns', from, to, kind, purpose, direction, search, offset]

  const txns = useQuery<TxResponse>({
    queryKey: qKey,
    queryFn: () => fetch(buildUrl()).then(r => r.json()),
  })
  const filters = useQuery<TxFilters>({
    queryKey: ['cash', 'tx-filters'],
    queryFn: () => fetch('/api/cash/transaction-filters').then(r => r.json()),
    staleTime: 5 * 60_000,
  })

  const d = txns.data
  const totalPages  = d ? Math.ceil(d.total_rows / PAGE) : 0
  const currentPage = Math.floor(offset / PAGE) + 1

  function handleClassified(id: number, updated: Partial<TxRow>) {
    qc.setQueryData<TxResponse>(qKey, old => {
      if (!old) return old
      return { ...old, rows: old.rows.map(r => r.id === id ? { ...r, ...updated } : r) }
    })
    qc.invalidateQueries({ queryKey: ['cash', 'tx-filters'] })
    setClassifyId(null)
  }

  return (
    <div className="rounded-b-lg rounded-tr-lg border border-ink-200 bg-white p-5">
      <div className="text-[14px] font-semibold text-ink-800 mb-4">Chase transactions</div>

      <div className="flex flex-wrap gap-2 mb-4">
        <input type="date" value={from} onChange={e => { setFrom(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white" />
        <span className="self-center text-ink-400 text-[12px]">{t('cash.to')}</span>
        <input type="date" value={to} onChange={e => { setTo(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white" />

        <select value={kind} onChange={e => { setKind(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white font-medium">
          <option value="all">{t('cash.all_kinds')}</option>
          {filters.data?.kinds.map(k => (
            <option key={k.kind} value={k.kind!}>{k.kind} ({FMT_INT.format(k.count)})</option>
          ))}
        </select>

        <select value={purpose} onChange={e => { setPurpose(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white font-medium min-w-[180px]">
          <option value="all">{t('cash.all_purposes')}</option>
          {filters.data?.purposes.map(p => (
            <option key={p.purpose} value={p.purpose!}>{p.purpose} ({FMT_INT.format(p.count)})</option>
          ))}
        </select>

        <select value={direction} onChange={e => { setDir(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white font-medium">
          <option value="all">{t('cash.in_plus_out')}</option>
          <option value="in">{t('cash.in_only')}</option>
          <option value="out">{t('cash.out_only')}</option>
        </select>

        <input type="text" placeholder={t('cash.search_description')} value={search}
          onChange={e => { setSearch(e.target.value); resetOffset() }}
          className="px-2.5 py-1.5 border border-ink-200 rounded-md text-[12px] bg-white w-52" />

        <button type="button" onClick={() => { setFrom(firstOfYear); setTo(today); setKind('all'); setPurpose('all'); setDir('all'); setSearch(''); setOffset(0) }}
          className="h-8 px-3 border border-ink-200 text-ink-500 rounded-md text-[12px] hover:bg-ink-50 transition-colors">Clear</button>
      </div>

      {d && (
        <div className="flex gap-6 mb-4 px-3 py-2.5 bg-ink-50 rounded-lg text-[12px]">
          <span><span className="text-ink-500">Rows:</span> <strong>{FMT_INT.format(d.total_rows)}</strong></span>
          <span><span className="text-ink-500">In:</span> <strong className="text-mint-600">{fmtMoney(Number(d.sum_in_cents), currency)}</strong></span>
          <span><span className="text-ink-500">Out:</span> <strong className="text-rose-600">{fmtMoney(Number(d.sum_out_cents), currency)}</strong></span>
          <span><span className="text-ink-500">Net:</span> <strong className={Number(d.sum_in_cents) + Number(d.sum_out_cents) >= 0 ? 'text-mint-600' : 'text-rose-600'}>{fmtMoney(Number(d.sum_in_cents) + Number(d.sum_out_cents), currency)}</strong></span>
        </div>
      )}

      {txns.isLoading
        ? <div className="flex items-center justify-center h-32 text-ink-400 text-[13px]">{t('common.loading')}</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                  <th className="text-left py-2 pr-3">{t('cash.date_col')}</th>
                  <th className="text-right py-2 pr-3">{t('cash.amount_col')}</th>
                  <th className="text-left py-2 pr-3">{t('cash.kind_col')}</th>
                  <th className="text-left py-2 pr-3">{t('cash.counterparty_col')}</th>
                  <th className="text-left py-2 pr-3">{t('cash.purpose_col')}</th>
                  <th className="text-left py-2 pr-3">{t('cash.description_col')}</th>
                  <th className="text-right py-2 pr-3">{t('cash.balance_col')}</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {d?.rows.map(r => (
                  <>
                    <tr key={r.id}
                      className={`border-b border-ink-100 hover:bg-ink-50 ${classifyId === r.id ? 'bg-amber-50' : ''}`}>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{r.posting_date}</td>
                      <td className={`py-1.5 pr-3 text-right num font-medium whitespace-nowrap ${r.amount_cents < 0 ? 'text-rose-600' : 'text-mint-600'}`}>
                        {fmtMoney(r.amount_cents, currency)}
                      </td>
                      <td className="py-1.5 pr-3">
                        <span className={`pill ${pillForKind(r.counterparty_kind)}`}>{r.counterparty_kind ?? '—'}</span>
                      </td>
                      <td className="py-1.5 pr-3 max-w-[160px] truncate" title={r.counterparty_canonical ?? ''}>
                        {r.counterparty_canonical ?? <span className="text-ink-400">—</span>}
                      </td>
                      <td className="py-1.5 pr-3 text-ink-500 max-w-[160px] truncate" title={r.business_purpose ?? ''}>
                        {r.business_purpose ?? <span className="text-ink-400">—</span>}
                      </td>
                      <td className="py-1.5 pr-3 text-ink-400 max-w-[220px] truncate" title={r.description_preview}>
                        {r.description_preview}
                      </td>
                      <td className="py-1.5 pr-3 text-right num text-ink-500 whitespace-nowrap">
                        {r.balance_cents != null ? fmtMoney(r.balance_cents, currency) : ''}
                      </td>
                      <td className="py-1.5 text-right">
                        {needsClassify(r) && (
                          <button
                            onClick={() => setClassifyId(classifyId === r.id ? null : r.id)}
                            className={`px-2 py-1 rounded text-[11px] font-semibold border transition-colors
                              ${classifyId === r.id
                                ? 'bg-amber-100 border-amber-300 text-amber-800'
                                : 'bg-white border-ink-200 text-ink-600 hover:border-amber-400 hover:text-amber-700'}`}>
                            {classifyId === r.id ? 'Cancel' : 'Classify'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {classifyId === r.id && (
                      <ClassifyPanel
                        row={r}
                        onDone={updated => handleClassified(r.id, updated)}
                        onCancel={() => setClassifyId(null)}
                      />
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )
      }

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-ink-100">
          <button onClick={() => setOffset(Math.max(0, offset - PAGE))} disabled={offset === 0}
            className="px-3 py-1.5 border border-ink-200 rounded-md text-[12px] font-medium disabled:opacity-40 hover:bg-ink-50">
            {t('common.previous')}
          </button>
          <span className="text-[12px] text-ink-500">
            {t('cash.page_of', { current: currentPage, total: totalPages, rows: FMT_INT.format(d?.total_rows ?? 0) })}
          </span>
          <button onClick={() => setOffset(offset + PAGE)} disabled={currentPage >= totalPages}
            className="px-3 py-1.5 border border-ink-200 rounded-md text-[12px] font-medium disabled:opacity-40 hover:bg-ink-50">
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  )
}

function SourceRow({ label, status, line, pill }: {
  label: string; status: 'green' | 'amber' | 'red'; line: string; pill: string
}) {
  const dotColor = status === 'green' ? 'bg-mint-500' : status === 'amber' ? 'bg-amber-500' : 'bg-rose-500'
  const pillClass = status === 'green' ? 'pill-mint' : status === 'amber' ? 'pill-honey' : 'pill-flame'
  return (
    <div className="flex items-center justify-between px-3 py-2.5 border border-ink-200 rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${dotColor}`} />
        <div>
          <div className="font-semibold text-[13px] text-ink-800">{label}</div>
          <div className="text-[11px] text-ink-500">{line}</div>
        </div>
      </div>
      <span className={`pill ${pillClass}`}>{pill}</span>
    </div>
  )
}
